import type { SkillRequirements } from "./types.js";

/**
 * Generate a DSL-format preamble from skill requirements.
 *
 * The preamble is prepended to the skill prompt when the skill runs standalone
 * (without an orchestrator), giving the agent an explicit checklist of what to
 * verify before starting work. Each section maps to a requirement category:
 *
 * - `REQUIRES` — file/directory gates (`hard:` or `soft:` prefix)
 * - `INPUT`    — user input fields, with a pointer to `input.json` if `skillId` is provided
 * - `READS`    — informational read sources (not gated)
 * - `WRITES`   — files the skill will produce
 * - `RESOURCE` — runtime resource dependencies
 *
 * Sections with no entries are omitted. Returns an empty string when all
 * requirement arrays are empty.
 *
 * @param requirements - Parsed requirements (output of `parseSkillRequirements`)
 * @param skillId - Optional skill identifier; when provided, the INPUT section includes
 *   the canonical `input.json` path so the agent knows where to read collected values
 * @returns Multi-section DSL string, or `""` if there are no requirements to list
 *
 * @example
 * ```ts
 * const preamble = buildRequiresPreamble(requirements, "cf_overview");
 * const fullPrompt = preamble ? `${preamble}\n\n${skillBody}` : skillBody;
 * ```
 *
 * @docLink packages/resolver/api-reference#build-requires-preamble
 */
export function buildRequiresPreamble(requirements: SkillRequirements, skillId?: string): string {
  const sections: string[] = [];

  pushSection(sections, buildRequiresSection(requirements));
  pushSection(sections, buildInputSection(requirements, skillId));
  pushSection(sections, buildReadsSection(requirements));
  pushSection(sections, buildWritesSection(requirements));
  pushSection(sections, buildResourceSection(requirements));

  return sections.join("\n\n");
}

/** Append a section to the list when it produced output. */
function pushSection(sections: string[], section: string | null): void {
  if (section !== null) sections.push(section);
}

/** REQUIRES section (file gates). Returns `null` when there are no file gates. */
function buildRequiresSection(requirements: SkillRequirements): string | null {
  if (requirements.files.length === 0) return null;
  const lines = ["REQUIRES"];
  for (const f of requirements.files) {
    const prefix = f.gate === "hard" ? "hard" : "soft";
    lines.push(`  ${prefix}: ${f.path}  — ${f.description}`);
  }
  return lines.join("\n");
}

/** INPUT section (user inputs). Returns `null` when there are no inputs. */
function buildInputSection(requirements: SkillRequirements, skillId?: string): string | null {
  const allInputs = [...requirements.inputs_required, ...requirements.inputs_optional];
  if (allInputs.length === 0) return null;
  const lines = ["INPUT"];
  if (skillId) {
    lines.push(`  Read from: _concept/_grounding/${skillId}/input.json`);
    lines.push(
      `  If input.json is missing, ask the user for the following values and write the file:`,
    );
  }
  for (const input of allInputs) {
    const req = input.required ? "(required)" : "(optional)";
    const opts = input.options ? ` [${input.options.join(" | ")}]` : "";
    const def = input.default != null ? ` default: ${input.default}` : "";
    lines.push(`  - ${input.id}: ${input.label} ${req}${opts}${def}`);
    if (input.hint) lines.push(`    hint: ${input.hint}`);
  }
  return lines.join("\n");
}

/** READS section. Returns `null` when there are no read sources. */
function buildReadsSection(requirements: SkillRequirements): string | null {
  if (requirements.reads.length === 0) return null;
  const lines = ["READS"];
  for (const r of requirements.reads) {
    lines.push(`  ? ${r.path}  — ${r.description}`);
  }
  return lines.join("\n");
}

/** WRITES section (produces). Returns `null` when there is nothing produced. */
function buildWritesSection(requirements: SkillRequirements): string | null {
  if (requirements.produces.length === 0) return null;
  const lines = ["WRITES"];
  for (const p of requirements.produces) {
    lines.push(`  ${p.path}  — ${p.description}`);
  }
  return lines.join("\n");
}

/** RESOURCE section. Returns `null` when there are no resources. */
function buildResourceSection(requirements: SkillRequirements): string | null {
  if (!requirements.resources || requirements.resources.length === 0) return null;
  const lines = ["RESOURCE"];
  for (const r of requirements.resources) {
    const req = r.optional ? "(optional)" : "(required)";
    const ops = r.operations ? ` ops: [${r.operations.join(", ")}]` : "";
    lines.push(`  ${r.id} ${req}${ops}`);
    if (r.description) lines.push(`    — ${r.description}`);
  }
  return lines.join("\n");
}
